import { FREEZE, LOOP_FROM, LOOP_TO, PHASES, gateLabel } from '../../phases'
import type { EngagementView } from '../../stream/reducer'

interface RailProps {
  view: EngagementView

  /** Seconds in the current phase, for the live line. Null until a phase has been entered. */
  elapsed: number | null
}

/**
 * THE PROCESS RAIL, from `Frontend/engagement.html`.
 *
 * <p>`Specification/08 §3` requires the rail be "always visible, never behind a tab", and that
 * fired gates and flags be "a running list … things a reader must not have to hunt for". The
 * mockup's sixth correction merges them into one list, with gates indented under the phase that
 * raised them and the convergence loop drawn as a bracket — `Specification/01 §6.2`: "Phases 2, 3,
 * 4 and 5 are a convergence loop, not four stations."</p>
 *
 * <p><b>ALL TWELVE PHASES, ALWAYS</b> (`AC-UI-6`). An engagement at phase 3 has emitted three phase
 * events; a rail built from those alone would tell the reader the run is nearly over.</p>
 */
export function Rail({ view, elapsed }: RailProps): React.JSX.Element {
  const rows = PHASES.map((phase) => ({ phase, glyph: glyphFor(view, phase.number) }))

  return (
    <nav className="rail" aria-label="Process">
      <div className="scroll">
        <Attention view={view} />

        <h2 className="mlabel">Process</h2>

        {rows.filter((r) => r.phase.number < LOOP_FROM).map(({ phase, glyph }) => (
          <PhaseRow key={phase.number} phase={phase} glyph={glyph} view={view} />
        ))}

        <div className="loop">
          {/* THE PASS COUNTER, not a "(visit 2)" suffix. A second pass through the loop is a
              property of the loop, and reading it off one phase row hides that the other three
              went round with it. */}
          <p className="loopcap">
            <span aria-hidden="true">↻</span>
            converging · <span className="pass">pass {pass(view)}</span>
          </p>

          {rows.filter((r) => r.phase.number >= LOOP_FROM && r.phase.number <= LOOP_TO)
               .map(({ phase, glyph }) => (
                 <PhaseRow key={phase.number} phase={phase} glyph={glyph} view={view} />
               ))}
        </div>

        {rows.filter((r) => r.phase.number > LOOP_TO && r.phase.number < FREEZE)
             .map(({ phase, glyph }) => (
               <PhaseRow key={phase.number} phase={phase} glyph={glyph} view={view} />
             ))}

        {/* THE FREEZE IS AN ORDINARY ROW with a hairline above it. Everything below is computed
            from a specification that cannot change, and that is the whole of what the line says. */}
        <hr className="boundary" />

        {rows.filter((r) => r.phase.number >= FREEZE).map(({ phase, glyph }) => (
          <PhaseRow key={phase.number} phase={phase} glyph={glyph} view={view} />
        ))}
      </div>

      {/* ONE LIVE LINE, REPLACED IN PLACE. `§6.5`: "Not a log, not a growing list." */}
      <div className="prog" data-src="harness" role="status" aria-live="polite">
        <p className="line">
          <span className="g" aria-hidden="true">●</span>
          <span className="txt">{view.progress ?? activityLine(view)}</span>
          {elapsed !== null && <span className="el">{duration(elapsed)}</span>}
        </p>
      </div>
    </nav>
  )
}

/** The pinned block: what is waiting, and for how long. `§3.3.3`. */
function Attention({ view }: { view: EngagementView }): React.JSX.Element | null {
  const flags = view.flags.length

  if (view.terminal !== null) {
    const signed = view.lifecycle === 'signed'

    // `AC-ENG-8` — A FAULT IS RENDERED AS INTERRUPTED, WITH A CORRELATION ID, AND IS NEVER
    // RENDERED AS A REFUSAL OR A HALT. "Stopped" reads as a halt: it says the engagement reached an
    // end, and a fault is the opposite — the machinery failed and nothing was concluded (B-172).
    const faulted = view.lifecycle === 'faulted'
    const error = view.terminal['error'] as { message?: unknown; correlationId?: unknown } | undefined

    return (
      <section className="attn" aria-label="Attention">
        <p className="mlabel">Attention</p>
        <p className="open">
          <span className={`g ${signed ? 'g-done' : 'g-halt'}`} aria-hidden="true">
            {signed ? '✓' : '✗︎'}
          </span>
          {signed ? 'Signed and released' : faulted ? 'Interrupted' : 'Stopped'}
        </p>
        <p className="age">
          {faulted
            ? String(error?.message
                     ?? 'This engagement stopped because the system failed, not because it '
                        + 'reached a conclusion.')
            : String(view.terminal['reason'] ?? '')}
        </p>

        {/* THE CORRELATION ID IS THE POINT OF THE ROW. Without it a person reporting the fault has
            nothing to quote, and the line is an apology rather than a handle. */}
        {faulted && typeof error?.correlationId === 'string' && error.correlationId.length > 0 && (
          <p className="age" style={{ fontFamily: 'var(--f-mono)', fontSize: '11px' }}>
            {error.correlationId}
          </p>
        )}

        {/* ONLY WHEN THERE IS SOMETHING TO JUMP TO. This link was unconditional, and `ArtefactCard`
            renders nothing when the list is empty — so every terminal engagement offered
            "Go to artefacts ↓" pointing at an `#artefacts` id that did not exist on the page. On a
            HALTED run that is still the normal case: it stopped before phase 11, so there is no
            report to go to, and a link that does nothing is worse than no link. */}
        {view.artefacts.length > 0 && (
          <a className="btn jump" href="#artefacts">
            Go to artefacts <span className="arrow" aria-hidden="true">↓</span>
          </a>
        )}
        {flags > 0 && (
          <p className="flags">
            <span className="g g-halt" aria-hidden="true">⚠︎</span>
            {flags} open flag{flags === 1 ? '' : 's'}, printed beside the figures
          </p>
        )}
      </section>
    )
  }

  if (view.gate === null) return null

  return (
    <section className="attn" aria-label="Attention">
      <p className="mlabel">Attention</p>

      {/* THE GATE KIND, NEVER A GENERIC "awaiting input". Which question is waiting decides
          whether a reader opens it now or after lunch. */}
      <p className="open">
        <span className="g g-await" aria-hidden="true">⛔︎</span>
        <b>{title(view.openGateKind ?? 'gate')}</b>
      </p>

      {/* "A gate that has been open for six days is the single most actionable fact the list
          holds." Absent when the server did not say — an age is not something to guess. */}
      {view.openedAt !== null && <p className="age">open {age(view.openedAt)}</p>}

      <a className="btn primary jump" href="#gate">Answer <span className="arrow" aria-hidden="true">↓</span></a>

      {flags > 0 && (
        <p className="flags">
          <span className="g g-halt" aria-hidden="true">⚠︎</span>
          {flags} flag{flags === 1 ? '' : 's'} carried
        </p>
      )}
    </section>
  )
}

function PhaseRow({ phase, glyph, view }:
                  { phase: (typeof PHASES)[number]; glyph: string; view: EngagementView })
  : React.JSX.Element {
  const visit = view.visits[phase.number]
  const current = view.phase === phase.number && view.terminal === null
  const done = visit !== undefined && !current
  const ordinal = (visit?.entryOrdinal ?? 0) + 1

  return (
    <>
      <div className={`ph${current ? ' current' : done ? ' done' : ''}`}>
        <span className={`g ${glyph}`} aria-hidden="true">{glyphText(glyph)}</span>
        <span className="n" data-n={phase.number}>{phase.number}</span>
        <span>{phase.name}</span>

        {/* A SECOND VISIT IS VISIBLY A SECOND VISIT, and it is a BADGE rather than a sub-list:
            the per-visit history is history, not position, and it lives in the drawer. */}
        {ordinal > 1 && (
          <span className="revisit" title={`Entered ${ordinal} times. The full log is in the specification panel.`}>
            ↻{ordinal}
          </span>
        )}
      </div>

      {phase.gates.map((kind) => {
        const outcome = view.gateOutcomes[kind]
        const open = view.openGateKind === kind && view.gate !== null
        return (
          <div key={kind}
               className={`attach${open ? ' open' : outcome === undefined ? ' skipped' : ''}`}
               title={outcome ?? (open ? 'awaiting your decision' : 'not raised')}>
            <span className="k">{gateLabel(kind)}</span>
            <span className="v">{open ? 'OPEN' : outcome ?? ''}</span>
          </div>
        )
      })}
    </>
  )
}

/**
 * The glyph class for a phase row.
 *
 * <p>FIVE STATES, AND THEY DIFFER BY GLYPH AS WELL AS COLOUR. `eia-brand.css` gives each its own
 * character, so the rail is readable in greyscale and to a reader who cannot distinguish the
 * colours — which `§3.7` requires of every status in this system.</p>
 */
function glyphFor(view: EngagementView, phase: number): string {
  if (view.phase === phase && view.terminal !== null) {
    return view.lifecycle === 'signed' ? 'g-done' : 'g-halt'
  }
  if (view.phase === phase) return view.gate !== null ? 'g-await' : 'g-current'
  if (view.visits[phase] !== undefined) return 'g-done'
  return 'g-pending'
}

function glyphText(glyph: string): string {
  switch (glyph) {
    case 'g-done': return '✓'
    case 'g-current': return '●'
    case 'g-await': return '⛔︎'
    case 'g-halt': return '✗︎'
    default: return ''
  }
}

/** How many times the convergence loop has been round, from the deepest re-entry in it. */
function pass(view: EngagementView): string {
  let most = 0
  for (let n = LOOP_FROM; n <= LOOP_TO; n++) {
    most = Math.max(most, (view.visits[n]?.entryOrdinal ?? 0) + 1)
  }
  return String(Math.max(most, 1))
}

/** What to say on the live line when the run has reported no progress of its own. */
function activityLine(view: EngagementView): string {
  // THE LIVE LINE AGREES WITH THE PANEL ABOVE IT. `AC-ENG-8` again: a fault is interrupted, and
  // this line saying "Stopped" beside a panel saying "Interrupted" would be the two-surfaces
  // problem in miniature, inside one component (B-172).
  if (view.terminal !== null) {
    if (view.lifecycle === 'signed') return 'Signed'
    return view.lifecycle === 'faulted' ? 'Interrupted' : 'Stopped'
  }

  if (view.gate !== null) return 'Waiting for your decision'
  if (view.phaseName !== null) return `Working on ${view.phaseName}`
  return 'Starting'
}

function title(kind: string): string {
  const words = kind.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** An age a person reads, from an ISO instant. Days, then hours, then minutes. */
export function age(iso: string): string {
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (seconds < 90) return 'moments'
  if (seconds < 5400) return `${Math.round(seconds / 60)}m`
  if (seconds < 172800) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)} days`
}

function duration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}
